import React from "react";
import {
  LENGTH_PRESETS,
  MAX_SHEET_LENGTH_IN,
  MIN_SHEET_LENGTH_IN,
  MAX_UPLOADS,
  clamp,
} from "../../utils/gangsheetMath";
import {
  ActionButton,
  SectionTitle,
  controlLabelStyle,
  dangerButtonStyle,
  inputStyle,
  panelStyle,
  primaryButtonStyle,
  secondaryButtonStyle,
  subtlePanelStyle,
} from "./GangsheetUI";

type Props = {
  isMobile: boolean;
  sheetLengthIn: number;
  setSheetLengthIn: React.Dispatch<React.SetStateAction<number>>;
  spacingIn: number;
  setSpacingIn: React.Dispatch<React.SetStateAction<number>>;
  allowAutoRotate: boolean;
  setAllowAutoRotate: React.Dispatch<React.SetStateAction<boolean>>;
  busy: boolean;
  itemsCount: number;
  selectedItemExists: boolean;
  canExport: boolean;
  isExporting: boolean;
  message: string;
  handleFiles: (files: FileList | null) => void;
  autoNest: () => void;
  exportPng: () => void;
  duplicateSelected: () => void;
  rotateSelected: () => void;
  removeSelected: () => void;
  clearAll: () => void;
};

export default function GangsheetControls(props: Props) {
  const {
    isMobile,
    sheetLengthIn,
    setSheetLengthIn,
    spacingIn,
    setSpacingIn,
    allowAutoRotate,
    setAllowAutoRotate,
    busy,
    itemsCount,
    selectedItemExists,
    canExport,
    isExporting,
    message,
    handleFiles,
    autoNest,
    exportPng,
    duplicateSelected,
    rotateSelected,
    removeSelected,
    clearAll,
  } = props;

  const normalizedMaxLength = Math.max(MAX_SHEET_LENGTH_IN, 164);
  const normalizedUploadLimit = Math.max(MAX_UPLOADS, 50);

  const mergedLengthPresets = Array.from(
    new Set([
      ...LENGTH_PRESETS,
      24,
      36,
      48,
      60,
      72,
      84,
      96,
      108,
      120,
      132,
      144,
      156,
      164,
    ])
  )
    .filter(
      (length) =>
        length >= MIN_SHEET_LENGTH_IN && length <= normalizedMaxLength
    )
    .sort((a, b) => a - b);

  const handleLengthChange = (value: number) => {
    setSheetLengthIn(
      clamp(
        Math.round(value),
        MIN_SHEET_LENGTH_IN,
        normalizedMaxLength
      )
    );
  };

  return (
    <div style={{ ...panelStyle, padding: 14, display: "grid", gap: 14 }}>
      <SectionTitle
        title="Builder Controls"
        subtitle="Set sheet, spacing, nesting, and uploads."
      />

      <div style={{ ...subtlePanelStyle, padding: 14, display: "grid", gap: 12 }}>
        <div
          style={{
            display: "grid",
            gap: 12,
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
          }}
        >
          <label style={{ display: "grid", gap: 6 }}>
            <span style={controlLabelStyle}>Roll Width</span>
            <input style={inputStyle} value={`22"`} disabled />
          </label>

          <label style={{ display: "grid", gap: 6 }}>
            <span style={controlLabelStyle}>Sheet Length Preset</span>
            <select
              style={inputStyle}
              value={
                mergedLengthPresets.includes(sheetLengthIn)
                  ? sheetLengthIn
                  : ""
              }
              onChange={(e) => handleLengthChange(Number(e.target.value))}
            >
              {!mergedLengthPresets.includes(sheetLengthIn) && (
                <option value="">Custom</option>
              )}
              {mergedLengthPresets.map((length) => (
                <option key={length} value={length}>
                  {length}"
                </option>
              ))}
            </select>
          </label>
        </div>

        <label style={{ display: "grid", gap: 8 }}>
          <span style={controlLabelStyle}>
            Sheet Length: {sheetLengthIn}"
          </span>
          <input
            type="range"
            min={MIN_SHEET_LENGTH_IN}
            max={normalizedMaxLength}
            step={1}
            value={sheetLengthIn}
            onChange={(e) => handleLengthChange(Number(e.target.value))}
          />
        </label>

        <label style={{ display: "grid", gap: 6 }}>
          <span style={controlLabelStyle}>
            Manual Length Input ({MIN_SHEET_LENGTH_IN}"–{normalizedMaxLength}")
          </span>
          <input
            type="number"
            min={MIN_SHEET_LENGTH_IN}
            max={normalizedMaxLength}
            step={1}
            value={sheetLengthIn}
            onChange={(e) => handleLengthChange(Number(e.target.value))}
            style={inputStyle}
          />
        </label>

        <label style={{ display: "grid", gap: 8 }}>
          <span style={controlLabelStyle}>
            Artwork Spacing: {spacingIn.toFixed(2)}"
          </span>
          <input
            type="range"
            min={0.1}
            max={0.5}
            step={0.05}
            value={spacingIn}
            onChange={(e) => setSpacingIn(Number(e.target.value))}
          />
        </label>

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: 12,
            borderRadius: 12,
            background: "rgba(15,23,42,0.72)",
            border: "1px solid rgba(96,165,250,0.14)",
          }}
        >
          <input
            type="checkbox"
            checked={allowAutoRotate}
            onChange={(e) => setAllowAutoRotate(e.target.checked)}
          />
          <span style={{ fontWeight: 700, color: "#dbeafe" }}>
            Allow auto-nest rotation
          </span>
        </label>

        <label style={{ display: "grid", gap: 8 }}>
          <span style={controlLabelStyle}>
            Upload Artwork (up to {normalizedUploadLimit})
          </span>
          <input
            type="file"
            accept="image/png,image/webp,image/jpeg"
            multiple
            onChange={(e) => handleFiles(e.target.files)}
            disabled={busy || itemsCount >= normalizedUploadLimit}
            style={inputStyle}
          />
        </label>

        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr 1fr" }}>
          <ActionButton
            style={primaryButtonStyle}
            onClick={autoNest}
            disabled={!itemsCount || busy}
          >
            Auto Nest
          </ActionButton>
          <ActionButton
            style={secondaryButtonStyle}
            onClick={exportPng}
            disabled={!canExport || isExporting}
          >
            {isExporting ? "Exporting..." : "Export PNG"}
          </ActionButton>
        </div>

        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr 1fr" }}>
          <ActionButton
            style={secondaryButtonStyle}
            onClick={duplicateSelected}
            disabled={!selectedItemExists}
          >
            Duplicate
          </ActionButton>
          <ActionButton
            style={secondaryButtonStyle}
            onClick={rotateSelected}
            disabled={!selectedItemExists}
          >
            Rotate 90°
          </ActionButton>
        </div>

        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr 1fr" }}>
          <ActionButton
            style={secondaryButtonStyle}
            onClick={removeSelected}
            disabled={!selectedItemExists}
          >
            Remove
          </ActionButton>
          <ActionButton
            style={dangerButtonStyle}
            onClick={clearAll}
            disabled={!itemsCount}
          >
            Clear All
          </ActionButton>
        </div>

        {message ? (
          <div
            style={{
              padding: 12,
              borderRadius: 14,
              background: "rgba(59,130,246,0.1)",
              border: "1px solid rgba(96,165,250,0.18)",
              color: "#dbeafe",
              lineHeight: 1.5,
              fontWeight: 700,
            }}
          >
            {message}
          </div>
        ) : null}
      </div>
    </div>
  );
}